import { type FC, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { cn } from "@/shared/utils/core";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Tag } from "@/shared/components/ui/tag";
import { currencyInput } from "@/shared/utils/currencyInput";
import { resolveBalance } from "@/shared/utils/balanceEstimates";

import { BalanceEstimateSection } from "./BalanceEstimateSection";

interface AddValueSectionProps {
  itemId: number;
  classification: "asset" | "debt";
  valueLabel: string;
  onSave: (id: number, updates: Record<string, unknown>) => Promise<unknown>;
}

export const AddValueSection: FC<AddValueSectionProps> = ({
  itemId,
  classification,
  valueLabel,
  onSave,
}) => {
  const [balance, setBalance] = useState("");
  const [unknown, setUnknown] = useState(false);
  const [estimateRange, setEstimateRange] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isDebt = classification === "debt";
  const balInput = currencyInput(balance, setBalance);
  const canSave = !isSaving && (!!balance || (unknown && !!estimateRange));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (unknown) {
        if (!estimateRange) {
          enqueueSnackbar("Select an estimated range.", { variant: "error" });
          setIsSaving(false);
          return;
        }
        await onSave(itemId, {
          is_estimated: true,
          balance_estimate_range: estimateRange,
          balance: resolveBalance(true, estimateRange, ""),
        });
      } else {
        const resolved = resolveBalance(false, "", balance);
        if (isNaN(resolved) || resolved === 0) {
          enqueueSnackbar("Enter a value or check 'I don't know the balance yet'.", {
            variant: "error",
          });
          setIsSaving(false);
          return;
        }
        await onSave(itemId, {
          balance: resolved,
          is_estimated: false,
          balance_estimate_range: null,
        });
      }
      setBalance("");
      setUnknown(false);
      setEstimateRange("");
    } catch {
      enqueueSnackbar("Couldn't save value. Please try again.", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="px-4 pt-4 sm:px-6 sm:pt-6">
      <div className="flex flex-col items-start gap-4 rounded-lg border border-primary bg-subtle p-6">
        <div className="flex w-full items-center justify-between">
          <h4>Add value</h4>
          <Tag color="orange" icon={AlertTriangle} iconStroke={2} className="text-2xs">
            Value required
          </Tag>
        </div>
        <div className="w-full">
          <p className="mb-1 text-2xs text-secondary">{valueLabel}</p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-tertiary">
              {isDebt ? "-$" : "$"}
            </span>
            <Input
              value={balInput.displayValue}
              onChange={balInput.handleChange}
              disabled={unknown}
              className={cn("text-sm", isDebt ? "pl-9" : "pl-7")}
            />
          </div>
        </div>
        <BalanceEstimateSection
          balanceUnknown={unknown}
          estimateRange={estimateRange}
          onBalanceUnknownChange={(checked) => {
            setUnknown(checked);
            if (checked) setBalance("");
            if (!checked) setEstimateRange("");
          }}
          onEstimateRangeChange={setEstimateRange}
        />
        <Button
          variant="brand"
          size="md"
          className="w-fit"
          onClick={handleSave}
          disabled={!canSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};
